import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

interface HelpProps {
  onNavigate?: (route: string) => void;
}

export function Help({ onNavigate }: HelpProps) {
  return (
    <div className="min-h-screen bg-background text-foreground py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto bg-card p-8 rounded-xl shadow-lg border border-border">

        <div className="max-w-none">
          <header className="mb-8 border-b">
            <h1 className="text-3xl font-bold text-foreground mb-4 text-center">
              ORbiT8 Documentation
            </h1>

          </header>

          <section id="introduction" className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-2">

              Introduction
            </h2>
            <div className="bg-accent/30 p-6 rounded-lg border border-accent">
              <p className="text-lg leading-relaxed">
                <span className="font-bold text-primary">ORbiT8</span> enables scanning of repositories for license
                compliance and vulnerabilities, providing users with detailed risk assessment scores such as license risk,
                vulnerability risk, operational risk, and technical debt. It offers comprehensive visibility into the
                security and compliance posture of applications, helping users to manage and mitigate risks
                effectively.
              </p>
            </div>
          </section>
          <section id="dashboard" className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-2">
              
              Dashboard
            </h2>
            <p className="text-lg leading-relaxed mb-8">
              The Dashboard serves as the central hub for monitoring application risk, technical
              debt, compliance, and operational risk. It offers at-a-glance statistics and visualizations to help users
              quickly identify key metrics, assess risks, and prioritize actions across Applications/Projects.
            </p>

            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4">
              <img
                alt="Dashboard Overview"
                src="/documentation-images/image7.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Complete Overview of the Application user has access
              </p>
            </div>

            <p className="text-lg leading-relaxed mb-8">
              The dashboard features a set of key metrics at the top, each presented as a dedicated
              tile. These tiles provide quick, at-a-glance information about critical aspects of applications, projects,
              scans, and associated risks. Below are a detailed explanation of each tile and its purpose:
            </p>

            <div className="overflow-x-auto rounded-xl border border-border shadow-sm mb-8">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="px-6 py-4 font-bold text-foreground">Tile Name</th>
                    <th className="px-6 py-4 font-bold text-foreground">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[
                    { name: 'Total Applications', desc: 'Number of applications User has access to' },
                    { name: 'Total Projects', desc: 'Number of active projects.' },
                    { name: 'Total Scans', desc: 'Count of active scans of the given Application->Projects' },
                    { name: 'Total Components', desc: 'Total number of components in the Application the user has access to' },
                    { name: 'Total Technical Debt', desc: 'Represents combined software quality and maintenance risk indicators as a single score.' },
                    { name: 'Overall Risk Score', desc: 'Measures project health based on code deprecation, activity, contributors, and popularity.' },
                    { name: 'Vulnerability Risk Score', desc: 'Quantifies the threat level from known vulnerabilities to prioritize fixes.' },
                    { name: 'License Risk Score', desc: 'Highlights compliance risks related to software license usage and business relevance.' },
                    { name: 'Operational Risk Score', desc: 'Measures stability and activity in repository components and contributors.' },
                    { name: 'Total Encryption Algorithms', desc: 'Count of encryption Algorithms detected in the codebase.' },
                    { name: 'Provenance', desc: 'Shows the geographic origins of contributors involved in the codebase.' },
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-accent/5 transition-colors">
                      <td className="px-6 py-4 font-semibold text-primary">{row.name}</td>
                      <td className="px-6 py-4 text-muted-foreground">{row.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          
          </section>
          <section id="severity-levels" className="mb-8">
          
            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4">
              <img
                alt="Severity levels"
                src="/documentation-images/image1.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Severity levels across different risks in the dashboard
              </p>
            </div>

            <p className="text-lg leading-relaxed mb-6">
              These graphs display the counts of issues categorized by severity levels, showing how
              many are critical, high, medium, or low across different risk categories.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-lg">
              <div className="bg-card p-6 rounded-lg border border-border">
                <h3 className="font-bold text-primary mb-2">Vulnerability Distribution</h3>
                <p className="text-muted-foreground">Shows how many security vulnerabilities fall into each severity level.</p>
              </div>
              <div className="bg-card p-6 rounded-lg border border-border">
                <h3 className="font-bold text-primary mb-2">License Risk Distribution</h3>
                <p className="text-muted-foreground">Displays the count of license compliance issues grouped by severity.</p>
              </div>
              <div className="bg-card p-6 rounded-lg border border-border">
                <h3 className="font-bold text-primary mb-2">Operational Risk Distribution</h3>
                <p className="text-muted-foreground">Illustrates operational risks distributed by severity level.</p>
              </div>
              <div className="bg-card p-6 rounded-lg border border-border">
                <h3 className="font-bold text-primary mb-2">Project Risk Landscape</h3>
                <p className="text-muted-foreground">Multi-dimensional risk analysis visualization.</p>
              </div>
            </div>
          </section>

          <section id="project-risk" className="mb-8">
         
            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Project Risk Landscape"
                src="/documentation-images/image11.png"
                className="max-w-4xl w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Project Risk Graphs against all the scores for the Operational risk score
              </p>
            </div>

            <p className="text-lg leading-relaxed mb-6">
              These charts provide visual insights into project risk relationships and Operational
              Risk score.
            </p>
            <div className="bg-accent/30 p-6 rounded-lg border border-accent">
              <p className="text-lg leading-relaxed">
                <span className="font-bold text-primary">Project Risk Landscape:</span> Shows how projects vary based on two selected risk metrics (such as Technical Debt
                and Vulnerability Score). The bubble size represents Operational Risk Score, and the X and Y axes can be
                changed to display different values, updating the graph accordingly.
              </p>
            </div>
          </section>
          <p className="c4"><span className="c0"></span></p>
          <section id="application-overview" className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-2">
              
              Application Detail Overview in the Dashboard
            </h2>
            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Risk Overview of Applications"
                src="/documentation-images/image4.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Risk Overview of Applications in the Dashboard
              </p>
            </div>

            <p className="text-lg leading-relaxed mb-6">
              This table provides a detailed risk overview for each application. It shows scores
              for vulnerability, license, operational risk and technical debt. Users can quickly assess the risk levels
              and project count associated with each application.
            </p>

            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Application details"
                src="/documentation-images/image8.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Application details and navigation
              </p>
            </div>

            <div className="bg-accent/30 p-6 rounded-lg border border-accent mb-8">
              <p className="text-lg leading-relaxed">
                Clicking on an application name in this table navigates the user to the <span className="font-bold text-primary">detailed application view page</span>.
              </p>
            </div>

            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Single Application Dashboard view"
                src="/documentation-images/image6.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Single Application Dashboard view
              </p>
            </div>

            <p className="text-lg leading-relaxed mb-8">
              From the Application Dashboard, user can view the Projects access the projects
              associated.
            </p>

            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="List of Projects"
                src="/documentation-images/image10.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: List of Projects from the Application Dashboard
              </p>
            </div>

            <div className="bg-accent/30 p-6 rounded-lg border border-accent">
              <p className="text-lg leading-relaxed">
                On click of the Project, user can view the <span className="font-bold text-primary">individual projects dashboard</span> as shown
                below.
              </p>
            </div>
          </section>

          <section id="project-dashboard" className="mb-8">
        
            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Project View Dashboard"
                src="/documentation-images/image3.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Project View Dashboard
              </p>
            </div>

            <p className="text-lg leading-relaxed mb-8">
              Along with the dashboard, the left panel gives quick access to major sections like
              <span className="font-bold text-primary"> Applications</span>,
              <span className="font-bold text-primary"> Projects, Scans, Components Hub</span>,
              and <span className="font-bold text-primary"> Team</span>.
            </p>

            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Sidebar components"
                src="/documentation-images/image20.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Navigation components
              </p>
            </div>

            <p className="text-lg leading-relaxed mb-8">
              On the top-right, you'll find key actions and controls, including a button to start a
              <span className="font-bold text-primary"> New Scan</span> and <span className="font-bold text-primary"> settings for
                notifications</span>, <span className="font-bold text-primary"> display mode</span>, and <span
                  className="font-bold text-primary"> user profile</span>. <br/>
                  Each of these elements will be explained individually in
              the following chapters.
            </p>
          </section>
          <section id="new-scan" className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-2">
              
              New Scan
            </h2>
            <p className="text-lg leading-relaxed mb-8">
              Upon selecting <span className="font-bold text-primary">"New Scan,"</span> the user can either select an existing
              application, project, and version or create new ones. After providing these details, the user will proceed
              to complete the business impact survey and subsequent steps in the scanning workflow.
            </p>

            {/* Step 1: Selection */}
            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-12 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="New Scan Selection"
                src="/documentation-images/image18.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: New Scan
              </p>
            </div>

            {/* Step 2: Survey */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Step 1:Survey Form
                </h3>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  This form collects information on the project's usage and primary users to assess its business impact.
                </p>
              </div>
              <div className="rounded-xl overflow-hidden border border-border shadow-md bg-muted/20 p-4">
                <img
                  alt="Survey Form"
                  src="/documentation-images/image12.png"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
              </div>
            </div>

            {/* Step 3: Policy Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 items-center">
              <div className="order-2 md:order-1 rounded-xl overflow-hidden border border-border shadow-md bg-muted/20 p-4">
                <img
                  alt="Policy Page"
                  src="/documentation-images/image9.png"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
              </div>
              <div className="order-1 md:order-2">
                <h3 className="text-2xl font-bold mb-4">Step 2: Policy Page</h3>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  At this step, the user can either select from existing license compliance policies or create custom policies to apply during the scan.
                </p>
              </div>
            </div>

            {/* Step 4: Custom Policy */}
            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-12 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Custom Policy Page"
                src="/documentation-images/image13.png"
                className="max-w-4xl w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Custom Policy Configuration
              </p>
              <p className="mt-4 text-center text-muted-foreground px-8">
                On this page, users can configure custom license policies by selecting license types
                and assigning severity levels for compliance assessment.
              </p>
            </div>

            {/* Step 5: Authentication */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Step 3: Scan Page for Authentication</h3>
                <p className="text-lg leading-relaxed text-muted-foreground mb-4">
                  At this stage, GitHub authentication is required to scan repositories. The user must authenticate with GitHub to provide access credentials. This page also lists prerequisites needed for scanning, such as a valid repository URL, branch name, and project association.
                  
                </p>
               
              </div>
              <div className="rounded-xl overflow-hidden border border-border shadow-md bg-muted/20 p-4">
                <img
                  alt="Authentication Page"
                  src="/documentation-images/image5.png"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
              </div>
            </div>

            {/* Step 6: Scan Execution */}
            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Scan Execution Page"
                src="/documentation-images/image16.png"
                className="max-w-4xl w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Scan Execution and Logs
              </p>
            </div>

            <p className="text-lg leading-relaxed mb-8">
              This page allows the user to select the type of security scan to perform—either a
              <strong> standard scan</strong> for license compliance and basic security analysis or a <strong>full scan</strong> for comprehensive compliance
              and vulnerability assessment. Progress and scan logs are displayed as the scan executes. This step lets the user select a scan type—standard for basic compliance and security, or full for comprehensive analysis.
            </p>
 <p className="text-lg leading-relaxed mb-8">
             Scan progress and logs are shown below as the process runs. </p>
<p className="text-lg leading-relaxed mb-8">
             Once the scan completes, the user can download comprehensive reports including ORT
                Standard reports and ORbiT8 custom reports for detailed analysis and review.</p>

          </section>
          <section id="application-portfolio" className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-2">
              
              Application Page
            </h2>
            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Application Portfolio Page"
                src="/documentation-images/image21.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Application Page
              </p>
            </div>

            <p className="text-lg leading-relaxed mb-8">
            This page displays the Applications section of the platform, providing an overview of all available applications in the portfolio.
                
              </p>
                <p className="text-lg leading-relaxed mb-8">
               Each application card includes its name, activity status, number of linked projects and scans, risk assessment scores (overall, vulnerability, license issues, operational risk, technical debt), business criticality, application type, development phase, and technology stack.
          
              </p>
  <p className="text-lg leading-relaxed mb-8">
                Users can search, filter, and sort applications at the top, as well as create new applications using the provided button.           
        
              </p>
                <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                      alt="New Application Button"
                    src="/documentation-images/image2.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: New Application Button
              </p>
            </div>
               <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                       alt="Application Creation Form"
                  src="/documentation-images/image14.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Application Creation Form
              </p>
            </div>
               
            <p className="text-lg leading-relaxed mb-8">
       This form allows the user to create a new application by providing its name, description, business criticality, application type, development phase, and selecting relevant technologies used.    </p>
               <p className="text-lg leading-relaxed mb-8">
        Required fields ensure all key details are captured before creating the application.
Once completed, the user can click "Create Application" to add the new entry to the portfolio.     </p>
            
            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Application Actions"
                src="/documentation-images/image22.png"
                  className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Application Actions
              </p>
            </div>
             <p className="text-lg leading-relaxed mb-8">
       Each card provides options such as adding to favourites, viewing related projects, viewing reports, editing, archiving, or deleting the application.   </p>
          </section>
          <section id="projects-management" className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-2">
              
              Projects
            </h2>
            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Projects Section"
                src="/documentation-images/image19.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Projects Sections
              </p>
            </div>

            <p className="text-lg leading-relaxed mb-8">
             The Projects section, displaying active projects in a card-based layout.
Each project card presents the project name, status, technology stack, and key risk assessment scores (overall, vulnerability, license issues, operational risk, and technical debt).
            </p>
  <p className="text-lg leading-relaxed mb-8">
            The section also includes search, filter, and sort options at the top, as well as indicators for total projects, active projects, scanned projects, and favourites, making it easy to manage and review project details. </p>
              <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                   alt="New Project Button"
                    src="/documentation-images/image17.png"
                    className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: New Project Creation
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 items-center">
              <div className="order-2 md:order-1 rounded-xl overflow-hidden border border-border shadow-md bg-muted/20 p-4">
                <img
                  alt="New Project Configuration"
                  src="/documentation-images/image23.png"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
              </div>
              <div className="order-1 md:order-2">
                <h3 className="text-2xl font-bold mb-4">New Project Creation</h3>
                <p className="text-lg leading-relaxed text-muted-foreground mb-4">
                The “New Project” button allows users to initiate the creation of a new project within the platform.
                </p>
              
              </div>
            </div>

             <p className="text-lg leading-relaxed mb-8">
           This form enables the creation of a new project by specifying the application, project name, description, repository configuration, and technology stack.  </p>
        
         <p className="text-lg leading-relaxed mb-8">
        Users must select the application, enter repository details, and define relevant technologies before submitting. </p>
         <p className="text-lg leading-relaxed mb-8">
        Options are available to either create the project or create and immediately start scanning.</p>
        
          </section>

          <section id="scans-overview" className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-2">
              
              Scans
            </h2>
         
            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Scans History"
                src="/documentation-images/scandashboard.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Scan Dashboard
              </p>
            </div>
   <p className="text-lg leading-relaxed mb-8">
              The Scans page provides an overview of all scans performed within the tool. It allows you to monitor scan status, results, and associated risk assessment scores in one place.

            </p>
            <h3 className="text-2xl font-bold mb-4">Key Features</h3>
            <div className="order-1 md:order-2">
              <h3 className="text-2xl font-bold mb-4">1. Scan List</h3>
              <p className="text-lg leading-relaxed text-muted-foreground mb-4">
                Displays all scans with details such as:                </p>
              <li className='ml-8'>
                <strong> Scan Name </strong>- Auto-generated identifier for the scan.
              </li>
              <li className='ml-8'>
                <strong> Associated Project/Application </strong>- Indicates which project the scan is linked to.
              </li>
              <li className='ml-8'>
                <strong> Timestamp </strong>- Date the scan was executed.

              </li>

            </div>
              
            <div className="order-1 md:order-2 mt-8">
              <h3 className="text-2xl font-bold mb-4">2. Scan Status</h3>
              <p className="text-lg leading-relaxed text-muted-foreground mb-4">
                Each scan shows its progress state:
              </p>
              <li className='ml-8'>
                <strong> Running </strong>- Scan is currently in progress.
              </li>
              <li className='ml-8'>
                <strong> Failed </strong> - Scan did not complete successfully.
              </li>
              <li className='ml-8'>
                <strong> Completed </strong>- Scan finished successfully.
              </li>
              </div>


            <div className="order-1 md:order-2 mt-8">
              <h3 className="text-2xl font-bold mb-4">3. Scan Details</h3>
                <p className="text-lg leading-relaxed text-muted-foreground mb-4">
                For each scan, the following data points are displayed:
                </p>
              <li className='ml-8'>
                <strong> Components scanned </strong>
              </li>
              <li className='ml-8'>
                <strong> Files scanned </strong>
              </li>

            </div>

            <div className="order-1 md:order-2 mt-8">
              <h3 className="text-2xl font-bold mb-4">4. Risk Assessment Scores</h3>
              <p className="text-lg leading-relaxed text-muted-foreground mb-4">
                Every scan card contains multiple dimensions of risk the same are available on each application and project dashboard:
                  </p>
              <li className='ml-8'>
                <strong> Overall Score </strong>- Aggregate of all risk indicators.
              </li>
              <li className='ml-8'>
                <strong> Vulnerability Score </strong> - Number and severity of vulnerabilities.
              </li>
              <li className='ml-8'>
                <strong> License Risk Score </strong>- Identified license compliance issues.
              </li>
              <li className='ml-8'>
                <strong> Operational Risk Score </strong> - Risks impacting operational stability.
              </li>
              <li className='ml-8'>
                <strong> Technical Debt </strong> - Measure of maintainability and code-related debt.
              </li>
                </div>

            <div className="order-1 md:order-2 mt-8">
              <h3 className="text-2xl font-bold mb-4">Navigation and Filtering</h3>

              <li className='ml-8'>
                Use the <strong>search bar</strong> to quickly find scans.
              </li>
              <li className='ml-8'>
                Apply filters by application or project for targeted views.
              </li>
              </div>


            <div className="order-1 md:order-2 mt-8">
              <h3 className="text-2xl font-bold mb-4">Actions</h3>

              <li className='ml-8'>
                <strong>Start a new scan </strong> by clicking <strong>+ New Scan.</strong>
              </li>
              <li className='ml-8'>
                Access detailed results, re-scan, or download reports for the particular scan actions via the <strong>menu (⋮)</strong> on each scan card.
              </li>
            </div>

          </section>

          <section id="components-hub" className="mb-16">
           <h2 className="text-3xl font-bold mb-6 text-foreground flex items-center gap-2">
       
              Components Hub
            </h2>
            <div className="rounded-xl overflow-hidden border border-border shadow-md mb-8 bg-muted/20 p-4 flex flex-col items-center">
              <img
                alt="Components Hub Page"
                src="/documentation-images/image15.png"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <p className="mt-4 text-center text-sm text-muted-foreground italic">
                Figure: Components Hub Page
              </p>
            </div>

            <p className="text-lg leading-relaxed mb-8">
              The <strong>Components Hub</strong> provides a centralized view of all components across applications and projects. It serves as the single place for analysing component data, applying filters, and generating customized reports.
            </p>
   <h3 className="text-2xl font-bold mb-4">Key Features</h3>
            {/* Key Features */}
            <div className="space-y-12">
              <div>
                <h3 className="text-2xl font-bold mb-4">1. Central Repository</h3>

                 <li className='ml-8'>
                 Displays all components discovered from scans. Aggregates data across Applications and Projects under each application.
              
              </li>
              <li className='ml-8'>
               Aggregates data across:
               <ul style={{listStyleType:"disc"}}>
                 <li className='ml-8'>
               <strong> Applications </strong>
              </li>
              <li className='ml-8'>
               <strong> Projects </strong>under each application
              </li>
                </ul>
              </li>
                <p className="text-lg leading-relaxed text-muted-foreground">
                 </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4">2. Component Details</h3>
                <p className="text-lg leading-relaxed text-muted-foreground mb-4">
                  Each row in the hub provides detailed information about a component, including:
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { label: 'Component Name', desc: 'Identifier of the dependency.' },
                    { label: 'Version', desc: 'Version of the component in use.' },
                    { label: 'License', desc: 'License type associated with the component.' },
                    { label: 'License Severity', desc: 'Indicates severity of detected license risks.' },
                    { label: 'Deprecated', desc: 'Shows if the component is deprecated.' },
                    { label: 'Fix Available', desc: 'Indicates whether a fix or update is available.' },
                    { label: 'Homepage URL', desc: 'Reference link for the component.' },
                    { label: 'CVSS Score', desc: 'CVSS score along with severity is highlighted.' },
                  ].map((item, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="font-bold text-primary min-w-[140px]">{item.label}:</span>
                      <span className="text-muted-foreground">{item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4">3. Filtering & Search</h3>
                <ul className="list-disc ml-8 space-y-2 text-lg text-muted-foreground">
                  <li>Filter across applications or projects for a focused view.</li>
                  <li>Apply column-specific filters (e.g., by license, severity, fix availability).</li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4">4. Report Generation</h3>
                <p className="text-lg leading-relaxed text-muted-foreground mb-4">
                  The Export feature allows users to generate tailored reports based on selected columns and filtered data. For example,following teams may prefer to export their specific domain related reports

              </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-accent/20 p-4 rounded-lg border border-accent/30">
                    <p className="font-bold text-primary mb-1">Security Teams</p>
                    <p className="text-sm text-muted-foreground">Export vulnerability-focused reports.</p>
                  </div>
                  <div className="bg-accent/20 p-4 rounded-lg border border-accent/30">
                    <p className="font-bold text-primary mb-1">Compliance Teams</p>
                    <p className="text-sm text-muted-foreground">Export license-compliance reports.</p>
                  </div>
                  <div className="bg-accent/20 p-4 rounded-lg border border-accent/30">
                    <p className="font-bold text-primary mb-1">Engineering Teams</p>
                    <p className="text-sm text-muted-foreground">Track deprecated or outdated components.</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4">5. Column Customization</h3>
                <ul className="list-disc ml-8 space-y-2 text-lg text-muted-foreground">
                  <li>Use the Columns option to choose which data points to display.</li>
                  <li>Customize the view to highlight relevant information for your use case.</li>
                </ul>
              </div>
            </div>

            <div className="mt-16 bg-yellow-500/10 border-l-4 border-yellow-500 p-6 rounded-r-lg">
              <p className="text-sm font-bold uppercase tracking-wider text-yellow-600 mb-2">Disclaimer</p>
              <p className="text-muted-foreground italic">
                The graphs, charts, and other visual representations included in this document are for illustrative purposes only. As the portal is still under development, the data and its presentation are subject to change without prior notice.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}